import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Dashboard } from '../components/dashboard';
import { RequireAuth } from '../components/require-auth';
import { RouteLoading } from '../components/route-loading';
import { usePermissions } from '../hooks/use-permissions';
import { AWS_GROUP } from '../types';
import { captureError } from '../utils/capture-error';
import { Home } from './home';
import { NotFound } from './not-found';
import { SignIn } from './sign-in';

const AppRoutes = {
  Account: React.lazy(() => import('./account/account-routes')),
  Admin: React.lazy(() => import('./admin/admin-routes')),
  Business: React.lazy(() => import('./business/business-routes')),
  Carriers: React.lazy(() => import('./carriers/carriers-routes')),
  Connections: React.lazy(() => import('./connections/connections-routes')),
  Resources: React.lazy(() => import('./resources/resources-routes')),
  Team: React.lazy(() => import('./team/team-routes')),
};

export function Root() {
  // Hooks
  const { checkPermissions } = usePermissions();

  // Query - Check for Maintenance
  const pathMaint = process.env.REACT_APP_MAINT_URL;
  const queryMaint = useQuery({
    enabled: pathMaint !== undefined,
    queryKey: [pathMaint],
    queryFn: async () => {
      if (pathMaint) {
        const body = { data: { origin: window.origin } };
        try {
          const response = await fetch(pathMaint, {
            body: JSON.stringify(body),
            headers: { 'Content-Type': 'application/json' },
            method: 'POST',
          });
          const data: {
            data: {
              maintenanceActive: boolean;
              maintenanceBegin: string;
              maintenanceEnd: string;
            };
          } = await response.json();

          return data.data || null;
        } catch (error) {
          // Fail open: if the status endpoint can't be reached, nobody is
          // locked out and the app loads as usual
          captureError({ data: { error } });
          return null;
        }
      }

      return null;
    },
  });

  const isAdmin = checkPermissions([
    AWS_GROUP.ReadAll,
    AWS_GROUP.Staff_ReadOnly,
    AWS_GROUP.Accounting,
    AWS_GROUP.Commissions,
    AWS_GROUP.CommissionApproval,
    AWS_GROUP.Marketing,
    AWS_GROUP.ARC_BillingSystem,
    AWS_GROUP.GlobalAdmin,
    AWS_GROUP.PermissionsAdmin,
    AWS_GROUP.Contracting,
    AWS_GROUP.Reconciliation,
  ]);

  // Agents cannot access the dashboard during Maintenance
  // Admins can access the dashboard during Maintentnace
  const displayAdminMaint =
    isAdmin &&
    queryMaint.data &&
    queryMaint.data.maintenanceActive &&
    queryMaint.data.maintenanceEnd;

  return (
    <React.Fragment>
      <Routes>
        <Route path="sign-in" element={<SignIn />} />

        <Route
          path="/"
          element={
            <RequireAuth isAdmin={isAdmin} maintData={queryMaint.data}>
              <Dashboard
                maintenanceEnd={
                  displayAdminMaint && queryMaint.data
                    ? queryMaint.data.maintenanceEnd
                    : null
                }
              />
            </RequireAuth>
          }
        >
          <Route path="home" element={<Home />} />

          <Route
            path="account/*"
            element={
              <React.Suspense fallback={<RouteLoading />}>
                <AppRoutes.Account />
              </React.Suspense>
            }
          />
          <Route
            path="business/*"
            element={
              <React.Suspense fallback={<RouteLoading />}>
                <AppRoutes.Business />
              </React.Suspense>
            }
          />
          <Route
            path="team/*"
            element={
              <React.Suspense fallback={<RouteLoading />}>
                <AppRoutes.Team />
              </React.Suspense>
            }
          />
          <Route
            path="carriers/*"
            element={
              <React.Suspense fallback={<RouteLoading />}>
                <AppRoutes.Carriers />
              </React.Suspense>
            }
          />
          <Route
            path="resources/*"
            element={
              <React.Suspense fallback={<RouteLoading />}>
                <AppRoutes.Resources />
              </React.Suspense>
            }
          />
          <Route
            path="connections/*"
            element={
              <React.Suspense fallback={<RouteLoading />}>
                <AppRoutes.Connections />
              </React.Suspense>
            }
          />
          <Route
            path="admin/*"
            element={
              isAdmin ? (
                <React.Suspense fallback={<RouteLoading />}>
                  <AppRoutes.Admin />
                </React.Suspense>
              ) : (
                <Navigate to="/home" replace />
              )
            }
          />

          <Route path="/" element={<Navigate to="home" />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </React.Fragment>
  );
}
